import React, {useCallback, useState} from 'react'

import {RiSubtractLine, RiAddLine} from '@remixicon/react'
import {media} from 'styled-bootstrap-grid'
import styled from 'styled-components'
import {palette} from 'styled-tools'
import {useMedia} from 'react-use'

import {formatPrice} from '@festi/utils/numbers'
import {gridTheme} from '@festi/common/themes'
import {MainIconButton} from '@festi/common/components/buttons'
import {PagoLoanCalculation, PagoLoanOptions} from '@festi/common/api/rest'
import {Modal} from '@festi/common/components/common'

import RangeSlider from '../RangeSlider'
import InfoBox from '../../common/redesign/InfoBox'
import PagoTerms from '../pagoRegistration/PagoTerms'
import {InstallmentsText} from './InstallmentsText'
import CalculationDataGrid from './CalculationDataGrid'

const Root = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  width: 100%;
`
const SliderContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding-bottom: 16px;

  ${media.md`
    gap: 16px;
    padding-bottom: 24px;
  `}
`

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
`

const Disclaimer = styled.p`
  font-size: 12px;
  color: ${palette('ui60Solid')};
`

const TermsLink = styled.span`
  text-decoration: underline;
  cursor: pointer;
`

const TermsContainer = styled.div`
  ${media.md`
    padding: 24px;
  `}
`

interface PagoInstallmentsOptionProps {
  loanCalculationData: PagoLoanCalculation
  isLoading: boolean
  loanInstallments: number
  setLoanInstallments: (loanInstallments: number) => void
  installmentsOptions: PagoLoanOptions
  inCheckout?: boolean
}

export default function PagoInstallmentsOption({
  loanCalculationData,
  isLoading,
  loanInstallments,
  setLoanInstallments,
  installmentsOptions,
  inCheckout = false,
}: PagoInstallmentsOptionProps) {
  const [openTermsModal, setOpenTermsModal] = useState(false)
  const isMobile = useMedia(`(max-width: ${gridTheme.breakpoints.md}px)`, false)

  const handleMinusClick = useCallback(() => {
    setLoanInstallments(loanCalculationData.installments - 1)
  }, [loanCalculationData?.installments, setLoanInstallments])

  const handlePlusClick = useCallback(() => {
    setLoanInstallments(loanCalculationData.installments + 1)
  }, [loanCalculationData?.installments, setLoanInstallments])

  const handleSliderChange = useCallback(
    (value: number) => {
      if (value !== loanCalculationData.installments) {
        setLoanInstallments(value)
      }
    },
    [loanCalculationData?.installments, setLoanInstallments],
  )

  const minInstallments = installmentsOptions?.minimumInstallments || 1
  const maxInstallments = installmentsOptions?.maximumInstallments || 2

  const installmentsResults = loanCalculationData?.installments || 0

  return (
    <Root>
      <InstallmentsText
        installments={installmentsResults}
        averageMonthlyPayment={formatPrice(
          loanCalculationData?.averageMonthlyPayment || 0,
        )}
        isLoading={isLoading}
      />

      <SliderContainer>
        <MainIconButton
          onClick={handleMinusClick}
          icon={RiSubtractLine}
          disabled={installmentsResults <= minInstallments || isLoading}
          aria-label="Minus"
          buttonVariant="ghost"
          size="small"
        />
        <RangeSlider
          value={isLoading ? loanInstallments : installmentsResults}
          min={minInstallments}
          max={maxInstallments}
          step={1}
          mainTicksCount={isMobile ? 3 : 5}
          onChange={handleSliderChange}
          disabled={isLoading}
        />
        <MainIconButton
          onClick={handlePlusClick}
          icon={RiAddLine}
          disabled={installmentsResults >= maxInstallments || isLoading}
          aria-label="Plus"
          buttonVariant="ghost"
          size="small"
        />
      </SliderContainer>

      <InfoWrapper>
        <CalculationDataGrid
          loanCalculationData={loanCalculationData}
          isLoading={isLoading}
        />

        {inCheckout ? (
          <Disclaimer>
            Kaup eru staðfest með rafrænum skilríkjum &#183;{' '}
            <TermsLink role="button" onClick={() => setOpenTermsModal(true)}>
              Skilmálar
            </TermsLink>{' '}
          </Disclaimer>
        ) : (
          <InfoBox
            variant="neutral"
            info="Allar upplýsingar eru eingöngu til viðmiðunar. Sérsniðin lánakjör þín
          verða reiknuð út í kaupferli."
            large
            subtleText
          />
        )}
      </InfoWrapper>

      <Modal
        title="Skilmálar"
        onRequestClose={() => setOpenTermsModal(false)}
        isOpen={openTermsModal}
        bottomMobile
      >
        <TermsContainer>
          <PagoTerms />
        </TermsContainer>
      </Modal>
    </Root>
  )
}
